/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { FileService } from '../../services/file.service';

type VersionChain = {
  latestId: string;
  versionIds: string[];
  latestName: string;
  year: number;
};

type VersionSelection = {
  left: string;
  right: string;
};

export const ComparePVPage = () => {
  const [files, setFiles] = useState<VersionChain[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<Record<string, VersionSelection>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVersionedTreaties = async () => {
      try {
        const res = await FileService.getVersionsChain();
        if (Array.isArray(res.data)) {
          setFiles(res.data);
          const initialSelections = Object.fromEntries(
            res.data.map((treaty) => [
              treaty.latestId,
              {
                left: 'v1',
                right: treaty.versionIds.length > 1 ? 'v2' : 'v1',
              },
            ])
          );
          setSelectedVersions(initialSelections);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVersionedTreaties();
  }, []);

  const handleChange = (treatyId: string, side: 'left' | 'right', value: string) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [treatyId]: {
        ...prev[treatyId],
        [side]: value,
      },
    }));
  };

  const otherSide = (side: 'left' | 'right') => (side === 'left' ? 'right' : 'left');

  const sides = ['left', 'right'] as const;

  return (
    <div>
      <h1 className="pb-4 text-4xl font-bold text-center sm:pt-0 sm:pb-7 sm:text-left text-primary">
        <span className="inline sm:hidden">Compare PV</span>
        <span className="hidden sm:inline">Compare Treaty Versions</span>
      </h1>

      {loading ? (
        <p className="mt-6 text-xl text-center text-primary">Loading...</p>
      ) : files.length === 0 ? (
        <p className="mt-6 text-xl text-center text-primary text-muted-foreground">
          No versions found.
        </p>
      ) : (
        <table className="sm:w-[90%] text-left bg-white border border-gray-200 rounded-lg shadow-sm text-sm sm:text-lg">
          <thead>
            <tr className="px-4 py-3 font-semibold border text-md bg-gray-50 text-muted-foreground">
              <th className="p-2 sm:p-3 w-[50%] sm:w-[41%]">Treaty Name</th>
              <th className="p-2 sm:p-3 w-[10%] sm:w-[15%]">Year</th>
              <th className="p-2 sm:p-3 w-[20%] sm:w-[12%]">Version 1</th>
              <th className="p-2 sm:p-3 w-[20%] sm:w-[12%]">Version 2</th>
              <th className="p-0 sm:p-3 sm:w-[15%]" />
            </tr>
          </thead>
          <tbody>
            {files.map((treaty) => {
              const selected = selectedVersions[treaty.latestId];
              if (!selected) return null;

              const handleCompare = () => {
                const leftIndex = parseInt(selected.left.replace('v', ''), 10) - 1;
                const rightIndex = parseInt(selected.right.replace('v', ''), 10) - 1;
                const leftId = treaty.versionIds[leftIndex];
                const rightId = treaty.versionIds[rightIndex];
                navigate(`${treaty.latestId}?left=${leftId}&right=${rightId}`);
              };

              return (
                <tr
                  key={treaty.latestId}
                  className="border-t sm:cursor-default"
                  onDoubleClick={() => {
                    if (window.innerWidth < 640) {
                      handleCompare();
                    }
                  }}
                >
                  <td className="p-2 text-sm sm:text-base text-primary">
                    <div className="leading-snug break-words line-clamp-2" title={treaty.latestName}>
                      {treaty.latestName}
                    </div>
                  </td>
                  <td className="p-2 sm:px-3">{treaty.year}</td>
                  {sides.map((side) => (
                    <td key={side} className="px-3 py-2">
                      <select
                        value={selected[side]}
                        onChange={(e) => handleChange(treaty.latestId, side, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded"
                      >
                        {treaty.versionIds
                          .map((id, index) => ({ id, label: `v${index + 1}` }))
                          .filter(({ label }) => label !== selected[otherSide(side)])
                          .map(({ label }) => (
                            <option key={label} value={label}>
                              {label}
                            </option>
                          ))}
                      </select>
                    </td>
                  ))}
                  <td className="p-2 text-center">
                    <Button onClick={handleCompare} size="sm" className="w-full sm:w-auto">
                      Compare
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
